<template>
	<calculate-component :current="currentStep" :hasResult="hasResult">
		<template #side>
			<side-form
				:loadId="loadId"
				:economicId="economicId"
				:economicAnalysisVOData="economicAnalysisVOData"
				@startAnalysis="startAnalysis"
				:inputDisabled="inputDisabled"
			></side-form>
		</template>
		<template #content>
			<div class="primary-title">
				<div class="title-left">
					<div class="font">经济分析结果</div>
					<div class="primary-title-line"></div>
				</div>
				<div class="title-right">
					<!-- 分析成功 -->
					<div class="succcess-tips" v-if="loading == 2 && showTips">
						<span class="left-icon">
							<img
								src="@/assets/images/success-icon.png"
								alt=""
							/>
						</span>
						经济分析分析已完成，点击右上角“生成报告”按钮，可查看&分享报告

						<span
							class="right-icon"
							@click="() => (showTips = !showTips)"
						>
							<CloseOutlined />
						</span>
					</div>

					<!-- 分析失败 -->
					<div class="error-tips" v-if="loading == 3">
						<span class="left-icon">
							<img src="@/assets/images/fail-icon.png" alt="" />
						</span>
						{{ errMsg }}
					</div>
				</div>
			</div>

			<div class="result">
				<!-- 分析未开始 -->
				<div v-if="loading == 0 || loading == 3" class="status-img">
					<img src="@/assets/images/empty_4.png" alt="" />
					<div class="info">
						<div class="info-title">暂无结果</div>
						<div class="second-title">
							{{
								versionData == "BETA"
									? "旧版数据不支持经济分析，请新建项目体验!"
									: "请先完成左侧内容填写，并进行经济分析"
							}}
						</div>
					</div>
				</div>
				<!-- 分析进行中 -->
				<div v-if="loading == 1" class="status-img">
					<!-- 动画区域 -->
					<div id="svgaA" class="svga"></div>
					<div class="info">
						<div class="load-title">经济分析中</div>
					</div>
				</div>
				<!-- 分析完成 -->
				<div
					v-if="loading > 1 && loading != 3"
					class="finish beautify_scroll"
				>
					<div class="top-box">
						<div
							class="info"
							v-for="(item, index) in infos"
							:key="index"
						>
							<div class="top">
								<div class="count">
									{{ infosData[item.props] || "-" }}
								</div>
								<div class="unit">{{ item.unit }}</div>
							</div>
							<div class="bottom">
								{{ item.label }}
							</div>
						</div>
					</div>
					<div class="info-table">
						<div class="title">历年放电收益</div>
						<history-income-table
							:yearProfitInfos="yearProfitInfosData"
						></history-income-table>
					</div>
					<div class="info-table">
						<div class="title">投资方现金流</div>
						<investor-cash-flow
							:investorCashFlows="investorCashFlowsData"
							:hasTax="hasTax"
						></investor-cash-flow>
					</div>
				</div>
			</div>
		</template>

		<!-- 功能按钮 -->
		<template #action-btn>
			<!-- :disabled="finallyStep > 3 || loading != 2" -->
			<a-button
				:disabled="loading != 2"
				type="primary"
				@click="makeReport"
			>
				{{ finallyStep > 3 ? "查看报告" : "生成报告" }}
			</a-button>
		</template>
	</calculate-component>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import CalculateComponent from "../../components/CalculateBox.vue";
import SideForm from "./components/SideForm.vue";
import HistoryIncomeTable from "../../components/HistoryIncomeTable.vue";
import InvestorCashFlow from "../../components/InvestorCashFlow.vue";

import {
	DownloadOutlined,
	UploadOutlined,
	InfoOutlined,
	CloseOutlined,
} from "@ant-design/icons-vue";

// 引入router
import { useRoute } from "vue-router";
const route = useRoute();
// 引入vuex
import { useStore } from "vuex";
const store = useStore();
// 引入动画库
import { Player as SVGAPlayer, Parser as SVGAParser } from "svgaplayerweb";
// 引入获取项目详情api
import { getProjectDetail } from "@/api/projects";
// 引入解密
import { decryptData } from "@/plugins/index.js";
// 引入api
import {
	startEconomic,
	getEconomicData,
	saveToReport,
} from "@/api/economic.js";
// 引入公共方法
import { toFixed } from "@/plugins/index.js";

// 是否有结果
const hasResult = ref(false);
// 当前步骤
const currentStep = ref(2);
// 记录下id
const loadId = ref(null);
// 经济分析id
const economicId = ref(null);
// 分析按钮状态  0未开始 1分析中 2分析结束 3分析失败
let loading = ref(0);
// 是否可编辑
const inputDisabled = ref(false);
const economicAnalysisVOData = ref(null);
let showTips = ref(true);
// 动画
let parser = null;
// 错误信息
let errMsg = ref("数据出错，经济分析失败");

const infosData = reactive({
	dayProfit: undefined,
	unitCapacityDayProfit: undefined,
	irr: undefined,
	lcos: undefined,
});

// 历年收益
const yearProfitInfosData = ref(null);

// 现金流
const investorCashFlowsData = ref(null);

// 是否有税
const hasTax = ref(false);

const finallyStep = ref(3);
const versionData = ref("V1");

const infos = [
	{
		label: "单位容量日均收益",
		unit: "元/(天·kWh)",
		props: "unitCapacityDayProfit",
	},
	{
		label: "日均收益",
		unit: "元/天",
		props: "dayProfit",
	},
	{
		label: "内部收益率 IRR ",
		unit: "%",
		props: "irr",
	},
	{
		label: "储能度电成本 LCOS",
		unit: "元/kWh",
		props: "lcos",
	},
];

// 开始分析
const startAnalysis = emit => {
	// 禁止修改任何参数
	updateInputDisableStatus(true);
	// 状态改为分析中
	hasResult.value = false;
	loading.value = 1;
	// 更新store中的项目步骤
	currentStep.value = 2;
	store.commit("updateProjectStep", 2);

	startEconomic({
		id: economicId.value,
		projectId: loadId.value,
		...emit,
	})
		.then(res => {
			console.log(res);
			const { id } = res;
			if (id) {
				economicId.value = id;
			}
			// 查询经济分析的结果
			// 开始动画
			setTimeout(() => {
				startAnimation();
			}, 100);
			getEcnomicData();
		})
		.catch(err => {
			console.log(err);
			console.log("error触发：");
			currentStep.value = 2.3;
			// 更新store中的项目步骤
			store.commit("updateProjectStep", 2.3);
			updateInputDisableStatus(false);
			hasResult.value = false;
			loading.value = 3;
			errMsg.value = err.msg;
		});
};

// 获取项目详情
const getProjectDetailData = (step = null) => {
	getProjectDetail({
		id: loadId.value,
		toStep: step ? step : 3,
	}).then(res => {
		const { name, id, economicAnalysisVO, step, version } = res;
		// 更新store中的项目步骤
		currentStep.value = 2;
		store.commit("updateProjectStep", 2);
		// 更新store中的breadcrumb信息
		store.commit("updateBreadCrumbInfo", {
			name,
		});
		finallyStep.value = step;
		versionData.value = version;
		if (version == "BETA") {
			updateInputDisableStatus(true);
		}
		if (economicAnalysisVO) {
			economicAnalysisVOData.value = economicAnalysisVO;
			economicId.value = economicAnalysisVO.id;
			// 状态改为分析中
			hasResult.value = false;
			loading.value = 1;

			// 开始动画
			setTimeout(() => {
				startAnimation();
			}, 100);
			// 查询经济分析的结果
			getEcnomicData();
		}
	});
};

// 获取结果
const getEcnomicData = () => {
	getEconomicData(loadId.value).then(res => {
		console.log(res);
		if (res) {
			// 禁止修改任何参数
			updateInputDisableStatus(false);
			if (res.errorFlag) {
			} else {
				const {
					economicAnalysisResult,
					yearProfitInfos,
					investorCashFlows,
					taxFlag,
				} = res;
				const { dayProfit, unitCapacityDayProfit, irr, lcos } =
					economicAnalysisResult;

				loading.value = 2;
				currentStep.value = 2.5;
				// 更新store中的项目步骤
				store.commit(
					"updateProjectStep",
					finallyStep.value == 9 ? 9 : 2.5
				);
				hasResult.value = true;

				// 更新顶部信息
				infosData.dayProfit = toFixed(dayProfit, 2);
				infosData.unitCapacityDayProfit = toFixed(
					unitCapacityDayProfit,
					4
				);
				infosData.irr = toFixed(irr, 2);
				infosData.lcos = toFixed(lcos, 4);

				// 更新表格信息
				console.log(yearProfitInfos);
				yearProfitInfosData.value = yearProfitInfos;
				investorCashFlowsData.value = investorCashFlows;
				hasTax.value = taxFlag ? true : false;
			}
		}
	});
};

// 生成报告
const makeReport = () => {
	if (finallyStep.value > 3) {
		const { projectInfo } = store.state;
		console.log(projectInfo);
		const { shareKey } = projectInfo;
		// 跳转到v1报告页
		if (versionData.value == "BETA") {
			window.open(
				`${window.location.origin}/report/?shareKey=${shareKey}`,
				"_blank"
			);
		} else {
			window.open(
				`${window.location.origin}/report/v1?shareKey=${shareKey}`,
				"_blank"
			);
		}
	} else {
		saveToReport(loadId.value).then(res => {
			console.log(res);
			if (res) {
				// 更新vuex中项目信息
				store.commit("updateProjectInfo", {
					id: loadId.value,
					step: 9,
					shareKey: res,
				});
				// 更新store中的项目步骤
				currentStep.value = 9;
				finallyStep.value = 9;
				store.commit("updateProjectStep", 9);

				updateInputDisableStatus(false);
				// 跳转到v1报告页
				window.open(
					`${window.location.origin}/report/v1?shareKey=${res}`,
					"_blank"
				);
			}
		});
	}
};

// 更新禁止输入状态
const updateInputDisableStatus = (val = false) => {
	// 获取step
	const { step } = store.state.projectInfo;
	let res = false;
	// 大于该步骤时，不允许修改任何参数
	if (step && step > 3) {
		res = true;
	} else {
		res = val;
	}
	inputDisabled.value = res;
};

/* 动画 */
let startAnimation = () => {
	parser = new SVGAParser();
	const container = document.getElementById("svgaA");
	parser.load("/svga/loading.svga", videoItem => {
		const player = new SVGAPlayer(container);
		player.setVideoItem(videoItem);
		player.startAnimation();
	});
};

onMounted(() => {
	const { id, step } = route.query;

	if (id) {
		loadId.value = decryptData(id) || null;
	}
	console.log(loadId.value);

	// 清空面包屑
	store.commit("updateBreadCrumbInfo", {});
	console.log(step);
	// 获取详情
	getProjectDetailData(decryptData(step ? step : 3));
});
</script>

<style lang="less" scoped>
@import "./style/index.less";
</style>
